import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import posthog from "posthog-js";
import { Button, Modal, Form, Input, Icon, Confirm, Message, Label, Popup } from "semantic-ui-react";
import DataTable from "../../../ui/data-table/DataTable";
import { GridPager } from "../../../pagination/GridPager";
import RowsPerPageSelect from "../../../ui/rows-per-page-select/RowsPerPageSelect";
import CommonPageHeader from "../../../ui/page-header/CommonPageHeader";
import SearchInput from "../../../ui/search-input/SearchInput";
import SelectFilter from "../../../ui/select-filter/SelectFilter";
import ExcelExportButton from "../../../ui/excel-export-button/ExcelExportButton";
import "rc-pagination/assets/index.css";

import * as designationActions from "../../../../domain/designation/designationActions";
import { Common } from "../../../../../utils";
import { useSubscriptionStatus } from "../../../../../hooks/useSubscriptionStatus";
import SubscriptionRestriction from "../../../subscriptionRestriction/SubscriptionRestriction";
import "../departments/department.css";
import "../../../../domain/documents/Documents.css";

const NAME_MAX_LENGTH = 250;

const Designation = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const designationReducer = useSelector((state) => state.designationReducer);
  const { isSuspended } = useSubscriptionStatus();
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: "", notes: "" });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [exportLoading, setExportLoading] = useState(false);

  const { record, isLoading, Error, message } = designationReducer;
  const totalItems = Array.isArray(record) ? record.length : 0;

  const filteredItems = useMemo(() => {
    const list = Array.isArray(record) ? record : [];
    const q = searchInput.trim().toLowerCase();
    return list.filter((item) => {
      const matchesSearch =
        q.length === 0 ||
        String(item.name || "").toLowerCase().includes(q) ||
        String(item.notes || "").toLowerCase().includes(q);
      const matchesStatus =
        statusFilter === "all" || (statusFilter === "active" ? item.isActive : !item.isActive);
      return matchesSearch && matchesStatus;
    });
  }, [record, searchInput, statusFilter]);

  const totalFiltered = filteredItems.length;
  const startRow = totalFiltered === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endRow = Math.min(currentPage * pageSize, totalFiltered);
  const paginationSummary =
    totalFiltered <= 0
      ? "No results"
      : totalFiltered === 1
        ? "Showing 1 of 1"
        : startRow === endRow
          ? `Showing ${startRow} of ${totalFiltered}`
          : `Showing ${startRow}-${endRow} of ${totalFiltered}`;

  const sortedItems = useMemo(() => {
    const list = [...filteredItems];
    const direction = sortDir === "asc" ? 1 : -1;
    const valueForSort = (item) => {
      switch (sortKey) {
        case "name":
          return String(item.name || "").toLowerCase();
        case "description":
          return String(item.notes || "").toLowerCase();
        case "createdDate": {
          const ts = item.createdDate ? new Date(item.createdDate).getTime() : 0;
          return Number.isNaN(ts) ? 0 : ts;
        }
        case "status":
          return item.isActive ? 1 : 0;
        case "userCount":
          return Number(item.userCount) || 0;
        default:
          return String(item[sortKey] || "").toLowerCase();
      }
    };
    list.sort((a, b) => {
      const va = valueForSort(a);
      const vb = valueForSort(b);
      if (va < vb) return -1 * direction;
      if (va > vb) return 1 * direction;
      return 0;
    });
    return list;
  }, [filteredItems, sortKey, sortDir]);

  const pagedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedItems.slice(start, start + pageSize);
  }, [sortedItems, currentPage, pageSize]);

  useEffect(() => {
    if (isSuspended) history.replace("/settings/company?tab=billing");
  }, [isSuspended, history]);

  useEffect(() => {
    if (!isSuspended) {
      const options = { start: 0, page: 1, take: 100 };
      dispatch(designationActions.DesignationListing(options, 1));
    }
  }, []);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(totalFiltered / pageSize));
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [totalFiltered, pageSize, currentPage]);

  if (isSuspended) {
    return (
      <div style={{ padding: "20px" }}>
        <SubscriptionRestriction actionKey="app.subscriptionRestriction.actionAccessSettings" />
      </div>
    );
  }

  const handleInputChange = (e, { name, value }) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
    if (name === "notes" && value && !/^[a-zA-Z0-9\s.,!?-]*$/.test(value)) {
      setFormErrors((prev) => ({ ...prev, notes: t("app.designations.notesValidation") }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = t("app.designations.nameRequired");
    else if (formData.name.length > NAME_MAX_LENGTH) errors.name = t("app.common.nameMaxChars", { max: NAME_MAX_LENGTH });
    if (formData.notes && !/^[a-zA-Z0-9\s.,!?-]*$/.test(formData.notes)) errors.notes = t("app.designations.notesValidation");
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      let response;
      if (editingItem) {
        response = await dispatch(
          designationActions.patchDesignation({
            id: editingItem.id,
            name: formData.name.trim(),
            notes: formData.notes.trim(),
            isActive: editingItem.isActive,
          })
        );
      } else {
        response = await dispatch(designationActions.saveDesignation(formData));
      }
      if (response?.success) {
        Common.showSuccessMessage(response?.message || (editingItem ? t("app.designations.successUpdated") : t("app.designations.successCreated")));
        setIsModalOpen(false);
        setEditingItem(null);
        setFormData({ name: "", notes: "" });
        setFormErrors({});
      } else {
        Common.showErrorMessage(response?.message || (editingItem ? "Update failed" : "Create failed"));
      }
    } catch {
      Common.showErrorMessage(t("app.designations.errorGeneric"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (row) => {
    setEditingItem(row);
    setFormData({ name: row.name, notes: row.notes || "" });
    setIsModalOpen(true);
  };

  const handleDelete = async (row) => {
    try {
      const result = await dispatch(designationActions.deleteDesignation(row));
      if (result?.success) Common.showSuccessMessage(result.message || t("app.designations.successDeactivated"));
      else Common.showErrorMessage(result?.message || t("app.designations.errorDeactivate"));
    } catch (error) {
      const apiMsg = error?.response?.data?.message ?? error?.response?.data?.Message ?? error?.message;
      Common.showErrorMessage(typeof apiMsg === "string" && apiMsg ? apiMsg : t("app.designations.errorDeactivate"));
    }
  };

  const handleReactivate = async (row) => {
    try {
      await dispatch(designationActions.patchDesignation({ ...row, isActive: true }));
      Common.showSuccessMessage(t("app.designations.successReactivated"));
    } catch (error) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("designationReactivate", {
          type: "exception",
          success: false,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
          status: error?.response?.status,
        });
      }
      Common.showErrorMessage(t("app.designations.errorReactivate"));
    }
  };

  const handleSortColumn = (key) => {
    if (key === "actions") return;
    if (sortKey === key) setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
    setCurrentPage(1);
  };

  const handleExport = () => {
    try {
      setExportLoading(true);
      const rows = [
        ["Name", "Description", "Created Date", "Status", "Associated Employees"],
        ...sortedItems.map((d) => [
          d.name || "",
          d.notes || "",
          d.createdDate ? new Date(d.createdDate).toLocaleDateString() : "N/A",
          d.isActive ? "Active" : "Inactive",
          String(d.userCount != null ? Number(d.userCount) : 0),
        ]),
      ];
      const csv = rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "designations.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExportLoading(false);
    }
  };

  const tableColumns = [
    { key: "name", header: t("app.designations.designationName"), render: (row) => row.name },
    { key: "description", header: t("app.designations.descriptionCol"), render: (row) => <div className="department-description">{row.notes || t("app.designations.noDescription")}</div> },
    { key: "createdDate", header: t("app.designations.createdDate"), render: (row) => <div className="department-date">{row.createdDate ? new Date(row.createdDate).toLocaleDateString() : t("app.designations.na")}</div> },
    { key: "status", header: t("app.designations.status"), render: (row) => <Label color={row.isActive ? "green" : "red"} size="tiny">{row.isActive ? t("app.users.active") : t("app.designations.inactive")}</Label> },
    { key: "userCount", header: t("app.designations.associatedEmployees"), render: (row) => <div className="department-user-count">{row.userCount != null ? Number(row.userCount) : 0}</div> },
    {
      key: "actions",
      header: t("app.common.actions"),
      render: (row) => (
        <div className="department-actions">
          <Popup content={t("app.designations.editItem")} trigger={<button type="button" className="documents-grid-action-btn documents-grid-action-btn--edit" onClick={() => handleEdit(row)}>Edit</button>} />
          {row.isActive ? (
            <Popup
              content={t("app.designations.deactivateItem")}
              trigger={
                <button
                  type="button"
                  className="documents-grid-action-btn documents-grid-action-btn--disable"
                  onClick={() => {
                    setDeletingItem(row);
                    setIsDeleteModalOpen(true);
                  }}
                >
                  Disable
                </button>
              }
            />
          ) : (
            <Popup content={t("app.designations.reactivateItem")} trigger={<button type="button" className="documents-grid-action-btn documents-grid-action-btn--view" onClick={() => handleReactivate(row)}>Enable</button>} />
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="documents-page container-fluid px-3 px-md-4 py-4">
      <CommonPageHeader
        barClassName="documents-header-bar"
        contentClassName="documents-header-content"
        leftClassName="documents-header-left"
        iconWrapperClassName="documents-header-icon-wrapper"
        textClassName="documents-header-text"
        titleClassName="documents-page-title"
        descriptionClassName="documents-page-description"
        rightClassName="documents-header-right"
        icon={<Icon name="certificate" className="m-0" />}
        title={t("app.designations.title")}
        description={t("app.designations.description")}
        rightContent={
          <>
            <div className="common-page-header-stat-card" title="Total designations">
              <span className="common-page-header-stat-label">Total designations</span>
              <span className="common-page-header-stat-value">{totalItems}</span>
            </div>
            <Button primary onClick={() => setIsModalOpen(true)} className="create-department-btn">
              <Icon name="plus" />
              {t("app.designations.addItem")}
            </Button>
          </>
        }
      />

      {Error && (
        <Message negative>
          <Message.Header>{t("app.designations.errorHeader")}</Message.Header>
          <p>{message}</p>
        </Message>
      )}

      <div className="documents-list-content">
        <div className="department-list-description">
          {isLoading ? (
            <div className="loading-container">
              <Icon name="spinner" loading size="large" />
              <p>{t("app.designations.loading")}</p>
            </div>
          ) : totalItems > 0 ? (
            <div className="table-container">
              <div className="documents-filter-bar department-grid-toolbar">
                <div className="documents-filter-bar-left">
                  <SearchInput
                    value={searchInput}
                    onChange={(next) => {
                      setSearchInput(next);
                      setCurrentPage(1);
                    }}
                    onClear={() => {
                      setSearchInput("");
                      setCurrentPage(1);
                    }}
                    placeholder="Search designation..."
                    ariaLabel="Search designations"
                    wrapperClassName="documents-filter-search-wrap"
                    inputClassName="documents-input documents-filter-search-input"
                    iconClassName="bi bi-search documents-filter-search-icon"
                    clearButtonClassName="documents-filter-search-clear"
                  />
                  <SelectFilter
                    value={statusFilter}
                    onChange={(next) => {
                      setStatusFilter(next);
                      setCurrentPage(1);
                    }}
                    options={[
                      { value: "all", label: "All statuses" },
                      { value: "active", label: "Active" },
                      { value: "inactive", label: "Inactive" },
                    ]}
                    ariaLabel="Filter by status"
                    wrapperClassName="documents-filter-office"
                    iconClassName="bi bi-funnel documents-filter-office-icon"
                    selectClassName="documents-input documents-filter-select"
                  />
                </div>
                <div className="documents-filter-bar-right">
                  <ExcelExportButton
                    className="documents-compliance-export-btn"
                    onClick={handleExport}
                    loading={exportLoading}
                    disabled={exportLoading}
                    title="Export designations to CSV"
                    label="Excel"
                    loadingLabel="Exporting..."
                  />
                  <RowsPerPageSelect
                    value={pageSize}
                    onChange={(next) => {
                      setPageSize(next);
                      setCurrentPage(1);
                    }}
                    options={[10, 20, 50, 100]}
                    label="Rows per page"
                    ariaLabel="Rows per page"
                    wrapperClassName="documents-filter-page-size"
                    labelClassName="documents-filter-page-size-label"
                    selectClassName="documents-input documents-filter-select documents-filter-page-size-ddl"
                  />
                </div>
              </div>
              <DataTable
                className="department-table"
                columns={tableColumns.map((column) => ({
                  key: column.key,
                  label: column.header,
                  sortable: column.key !== "actions",
                  headerClassName: column.key === "actions" ? "data-table-th-actions department-table-th-actions" : undefined,
                }))}
                rows={pagedItems}
                getRowKey={(row) => row.id}
                getRowClassName={(_, index) => (((currentPage - 1) * pageSize + index) % 2 === 0 ? "even-row" : "odd-row")}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSortColumn}
                emptyMessage={t("app.designations.emptyTitle")}
                renderRow={(row) => (
                  <>
                    {tableColumns.map((column) => (
                      <td key={`${row.id}-${column.key}`} className={column.key === "actions" ? "data-table-td-actions department-table-td-actions" : undefined}>
                        {column.render ? column.render(row) : row[column.key]}
                      </td>
                    ))}
                  </>
                )}
              />
              <div className="documents-pagination documents-pagination--grid-pager">
                <span className="documents-pagination-total">{paginationSummary}</span>
                <div className="documents-pagination-pager-wrap">
                  <GridPager
                    className="documents-rc-pagination"
                    onChange={(page) => setCurrentPage(page)}
                    current={currentPage}
                    total={totalFiltered}
                    pageSize={pageSize}
                    defaultPageSize={pageSize}
                    showLessItems
                    showTitle={false}
                    showSizeChanger={false}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <Icon name="certificate" size="huge" color="grey" />
              <h3>{t("app.designations.emptyTitle")}</h3>
              <p>{t("app.designations.emptyStateDescription")}</p>
            </div>
          )}
        </div>
      </div>

      <Modal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingItem(null);
          setFormData({ name: "", notes: "" });
          setFormErrors({});
        }}
        size="small"
        className="department-modal"
        closeOnEscape
        closeOnDimmerClick={false}
        dimmer={{ className: "department-modal-dimmer" }}
        style={{ height: "fit-content", margin: "auto", borderRadius: "20px" }}
      >
        <Modal.Header className="modal-header-brand modal-header-brand-gradient">
          <Icon name={editingItem ? "edit" : "plus"} />
          <span>{editingItem ? t("app.designations.editTitle") : t("app.designations.createTitle")}</span>
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label htmlFor="designation-name">{t("app.designations.nameRequiredLabel")}</label>
              <Input id="designation-name" name="name" value={formData.name} onChange={handleInputChange} placeholder={t("app.designations.enterName")} error={!!formErrors.name} autoFocus={!editingItem} maxLength={NAME_MAX_LENGTH} />
              <div className="field-helper-text" style={{ textAlign: "right", marginTop: "4px" }}>
                <small className="text-muted">{t("app.common.characterCount", { current: formData.name.length, max: NAME_MAX_LENGTH })}</small>
              </div>
              {formErrors.name && <Message negative size="tiny">{formErrors.name}</Message>}
            </Form.Field>
            <Form.Field>
              <label htmlFor="designation-notes">{t("app.designations.descriptionLabel")}</label>
              <Form.TextArea id="designation-notes" name="notes" value={formData.notes} onChange={handleInputChange} placeholder={t("app.designations.enterDescription")} rows={3} error={!!formErrors.notes} maxLength={1000} />
              <div className="field-helper-text">
                <small>{t("app.designations.notesHelper")}</small>
              </div>
              {formErrors.notes && <Message negative size="tiny">{formErrors.notes}</Message>}
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setIsModalOpen(false)} basic color="grey">
            {t("app.common.cancel")}
          </Button>
          <Button primary onClick={handleSubmit} loading={isSubmitting} disabled={isSubmitting}>
            {editingItem ? t("app.designations.update") : t("app.designations.create")}
          </Button>
        </Modal.Actions>
      </Modal>

      <Confirm
        open={isDeleteModalOpen}
        header={t("app.designations.confirmDeactivate")}
        content={t("app.designations.confirmDeactivateMessage", { name: deletingItem?.name || "" })}
        cancelButton={t("app.common.cancel")}
        confirmButton={t("app.designations.confirmDeactivateButton")}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setDeletingItem(null);
        }}
        onConfirm={() => {
          if (deletingItem) {
            handleDelete(deletingItem);
            setIsDeleteModalOpen(false);
            setDeletingItem(null);
          }
        }}
        size="small"
        className="delete-confirm-modal"
        dimmer={{ className: "department-modal-dimmer" }}
      />
    </div>
  );
};

export default Designation;
